import { isArray, uniq } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import {
  EmployeeCalendarEvent,
  EmployeeCalendarEventLegend,
  EmployeeCalendarLegendKeys,
  EmployeeCalendarLegendRows,
  EmployeeCalendarPersonWithLegalEntityId,
  getEmployeeCalendarPersonsFromEvents,
  useEmployeeAbsenceCalendarConfig,
} from '~/components/EmployeeCalendar';
import {
  SYSTEM_PUBLIC_HOLIDAY_EVENT,
  useDynamicAbsenceCalendarLegend,
  useEmployeeCalendarPublicHolidays,
} from '~/components/EmployeeCalendar/Absences';
import { useAllEmployees } from '~/data/employees';
import { Enum } from '~/data/enums';
import { AbsencesReportData } from '~/data/reports';
import {
  PersonnelGroupFilterIndex,
  PersonnelGroupMappedData,
  convertAbsenceReportDataToEmployeesAndEvents,
  getMappedPersonnelGroupValues,
  resolveCountsAndAbsentEmployees,
} from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/data';
import { usePersonnelGroupsFilters } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/PersonnelGroupsFilters/usePersonnelGroupsFilters';
import { useShowEmployeesModeSelector } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/ShowEmployeesModeSelector/useShowEmployeesModeSelector';

type PropsType = {
  startDate: Date;
  endDate: Date;
  data: AbsencesReportData[];
};

export const useAbsenceCalendarReport = (props: PropsType) => {
  const { data, startDate, endDate } = props;

  const { getEmployeeById } = useAllEmployees();

  // Process Loaded Report Data
  const { personnelGroupOptions, ...loadedReportData } = useMemo(() => {
    const allData = convertAbsenceReportDataToEmployeesAndEvents(
      data,
      getEmployeeById,
    );

    const personnelGroupOptions = data.reduce(
      (acc, reportEntry): PersonnelGroupMappedData<string[]> => {
        const mappedData = getMappedPersonnelGroupValues(reportEntry);

        const entries = Object.values(PersonnelGroupFilterIndex).map(
          (pgIndex) => {
            const previousValues = acc[pgIndex];
            const newValue = mappedData[pgIndex];
            const newValues = !!newValue
              ? uniq(previousValues.concat(newValue))
              : previousValues;

            return [pgIndex, newValues];
          },
        );

        return Object.fromEntries(entries);
      },
      {
        [PersonnelGroupFilterIndex.PG1]: [],
        [PersonnelGroupFilterIndex.PG2]: [],
        [PersonnelGroupFilterIndex.PG3]: [],
        [PersonnelGroupFilterIndex.PG4]: [],
        [PersonnelGroupFilterIndex.PG5]: [],
      } as PersonnelGroupMappedData<string[]>,
    );

    return {
      ...allData,
      ...resolveCountsAndAbsentEmployees(allData),
      personnelGroupOptions,
    };
  }, [data, getEmployeeById]);

  const isPersonnelGroupOptionDisabled = useCallback(
    (group: PersonnelGroupFilterIndex, option: Enum) => {
      const availableValues = personnelGroupOptions[group];

      return !availableValues.includes(option.code);
    },
    [personnelGroupOptions],
  );

  // Resolve UI Filtered Report Data
  const personnelGroupsFilters = usePersonnelGroupsFilters({
    isPersonnelGroupOptionDisabled,
  });
  const { checkPersonnelGroupFilteringValue, enabledPersonnelGroupFilters } =
    personnelGroupsFilters;

  const filterAbsencesReportData = useCallback(
    (reportData: AbsencesReportData) => {
      const mappedValues = getMappedPersonnelGroupValues(reportData);

      const filteringCheckResults = enabledPersonnelGroupFilters.map(
        (pgIndex) =>
          checkPersonnelGroupFilteringValue(pgIndex, mappedValues[pgIndex]),
      );

      return !filteringCheckResults.some((value) => !value);
    },
    [enabledPersonnelGroupFilters, checkPersonnelGroupFilteringValue],
  );

  const topFilteredData = useMemo(() => {
    const filteredEmployeeIds = uniq(
      data.filter(filterAbsencesReportData).map(({ fEmpId }) => fEmpId),
    );

    const filterEmployees = <
      T extends EmployeeCalendarPersonWithLegalEntityId | EmployeeCalendarEvent,
    >(
      entries: T[],
    ): T[] => {
      return entries.filter((entry) =>
        filteredEmployeeIds.includes(entry.employeeId),
      );
    };

    const filteredData = {
      employees: filterEmployees(loadedReportData.employees),
      events: filterEmployees(loadedReportData.events),
    };

    return {
      ...filteredData,
      ...resolveCountsAndAbsentEmployees(filteredData),
    };
  }, [data, loadedReportData, filterAbsencesReportData]);

  // Include Public Holidays
  const { publicHolidayEvents, isLoading: isLoadingPublicHolidays } =
    useEmployeeCalendarPublicHolidays({
      startDate,
      endDate,
      reportEmployees: topFilteredData.employees,
    });

  const { allEvents } = useMemo(() => {
    const allEvents = publicHolidayEvents.concat(topFilteredData.events);

    return {
      allEvents,
    };
  }, [topFilteredData, publicHolidayEvents]);

  const { dynamicLegends, getDynamicAbsenceLegend } =
    useDynamicAbsenceCalendarLegend({ events: allEvents });

  const calendarProps = useEmployeeAbsenceCalendarConfig({
    events: allEvents,
    eventColorLegends: dynamicLegends,
    getAbsenceLegend: getDynamicAbsenceLegend,
  });

  const legendRows = useMemo((): EmployeeCalendarLegendRows => {
    const getLegendKeys = (
      legends: EmployeeCalendarEventLegend[],
    ): EmployeeCalendarLegendKeys => legends.map((legend) => legend.key);

    const dynamicKeys = getLegendKeys(dynamicLegends);

    const otherKeys = getLegendKeys(
      calendarProps.legends?.filter(
        (legend) => !dynamicKeys.includes(legend.key),
      ) ?? [],
    );

    return [dynamicKeys, otherKeys];
  }, [dynamicLegends, calendarProps.legends]);

  const showEmployeesModeSelector = useShowEmployeesModeSelector({
    hasAbsentEmployees: topFilteredData.hasAbsentEmployees,
    hasNonAbsentEmployees: topFilteredData.hasNonAbsentEmployees,
  });

  // Toggle Legend Events
  const [hiddenLegendEvents, setHiddenLegendEvents] = useState<string[]>([]);

  const toggleLegendEvents = useCallback(
    (legendKey: string | string[]) => {
      if (isArray(legendKey)) {
        const show = legendKey.filter((key) =>
          hiddenLegendEvents.includes(key),
        );
        const hide = legendKey.filter(
          (key) => !hiddenLegendEvents.includes(key),
        );

        setHiddenLegendEvents((prevState) =>
          prevState.filter((key) => show.includes(key)).concat(hide),
        );
      } else {
        setHiddenLegendEvents((prevState) => {
          return prevState.includes(legendKey)
            ? prevState.filter((key) => key !== legendKey)
            : prevState.concat(legendKey);
        });
      }
    },
    [hiddenLegendEvents, setHiddenLegendEvents],
  );

  const { legendsWithVisibility, legendFiltered } = useMemo(() => {
    const legendsWithVisibility = calendarProps.legends?.map((legend) => {
      if (!!legend.visibilityToggle) {
        return {
          ...legend,
          visibilityToggle: {
            ...legend.visibilityToggle,
            isVisible: !hiddenLegendEvents.includes(legend.key),
          },
        };
      }

      return legend;
    });

    const hiddenEventConfigs = hiddenLegendEvents.map((key) => {
      const [category, ...codeSegments] = key.split('_');

      return {
        key,
        category,
        code: codeSegments.join('_'),
      };
    });
    const isEventHidden = (event: EmployeeCalendarEvent) => {
      const { eventCategory, eventCode } = event;
      return hiddenEventConfigs.some(({ category, code }) => {
        return category === eventCategory && code === eventCode;
      });
    };
    const legendFilteredEvents = allEvents.filter(
      (event) => !isEventHidden(event),
    );

    const legendFilteredAbsenceEmployees =
      hiddenLegendEvents.length > 0
        ? getEmployeeCalendarPersonsFromEvents(
            legendFilteredEvents.filter(
              (event) => event.eventCategory !== SYSTEM_PUBLIC_HOLIDAY_EVENT,
            ),
          )
        : topFilteredData.absentEmployees;

    return {
      legendFiltered: {
        absentEmployees: legendFilteredAbsenceEmployees,
        events: legendFilteredEvents,
      },
      legendsWithVisibility,
    };
  }, [
    calendarProps.legends,
    hiddenLegendEvents,
    allEvents,
    topFilteredData.absentEmployees,
  ]);

  return {
    counts: loadedReportData.counts,
    showEmployeesModeSelector,
    calendarProps: {
      ...calendarProps,
      listEmployees:
        showEmployeesModeSelector.value === 'ALL'
          ? topFilteredData.employees
          : legendFiltered.absentEmployees,
      events: legendFiltered.events,
      legends: legendsWithVisibility,
      toggleLegendEvents,
      legendsConfig: {
        legendRows,
      },
    },
    isLoading: isLoadingPublicHolidays,
    personnelGroupsFilters: {
      ...personnelGroupsFilters,
      isOptionDisabled: isPersonnelGroupOptionDisabled,
    },
  };
};
